import { Injectable } from '@angular/core';
import { AdminsApi, AssetsApi, Attribute, PagedAssetResponse } from 'src/app/hickory-api';
import { MapsApiLoader } from 'src/app/shared/google-maps';
import { MapLocation } from '../../models';
import { ApiError } from 'src/app/shared/model/utility';

@Injectable()
export class AlertLocationService {
  geocoder: google.maps.Geocoder;
  mapApiPromise: Promise<void>;

  constructor(
    private readonly mapsApi: MapsApiLoader,
    private readonly adminsApi: AdminsApi,
    private readonly assetsApi: AssetsApi,
  ) {
    this.mapApiPromise = this.mapsApi.load().then(() => {
      this.geocoder = new google.maps.Geocoder();
    });
  }

  lookupAddress(coords: google.maps.LatLngLiteral): Promise<string> {
    return new Promise((resolve, reject) => {
      this.mapApiPromise.then(() => {
        const request = (!coords || coords.lat === 0 && coords.lng === 0) ? null : { 'location': coords };
        this.geocoder.geocode(request, (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
          if (status === google.maps.GeocoderStatus.OK && !!results?.length && !!results[0]?.formatted_address?.length) {
            resolve(results[0].formatted_address);
          } else {
            reject(new Error('failed to geocode location'));
          }
        });
      });
    });
  }

  detectCurrentPosition(): Promise<google.maps.LatLngLiteral> {
    return new Promise((resolve, reject) => {
      if (navigator?.geolocation) {
        navigator.geolocation.getCurrentPosition((position: GeolocationPosition) => {
          resolve({ lat: position.coords.latitude, lng: position.coords.longitude });
        });
      } else {
        reject(new Error('failed to get current position'));
      }
    });
  }

  getAdminAssetLocation(adminId: number): Promise<MapLocation> {
    if (!adminId) return Promise.reject(new Error('invalid admin ID'));
    return this.getAdminAssetIds(adminId).then(assetIds => this.getAssetLocation(assetIds));
  }

  private getAdminAssetIds(adminId: number): Promise<string[]> {
    return new Promise((resolve, reject) => {
      this.adminsApi.getAdmins([adminId], null, null, null, null, null, true).subscribe({
        next: admins => {
          const assetIds = admins?.items?.find(a => !!a.assetIds?.length)?.assetIds;
          if (assetIds?.length) {
            resolve(assetIds);
          } else {
            reject(new Error('invalid response'));
          }
        },
        error: (e: ApiError) => reject(new Error(e.statusText))
      });
    });
  }

  private getAssetLocation(assetIds: string[]): Promise<MapLocation> {
    return new Promise((resolve, reject) => {
      if (!assetIds?.length) {
        reject(new Error('empty list of asset IDs'));
      } else {
        this.assetsApi.findAssets(1, 25, assetIds).subscribe({
          next: (assets) => this.processAssetsResponse(assets, resolve, reject),
          error: (err: ApiError) => reject(new Error(err.statusText)),
        });
      }
    });
  }

  private processAssetsResponse(assets: PagedAssetResponse, resolve: (value: MapLocation) => void, reject: (error: Error) => void) {
    let coords: google.maps.LatLngLiteral;
    let assetName: string;
    const locationRegex = /location/i;

    // Finds the first asset with a location
    assets?.items?.some((a) => {
      // Search asset for a location entry
      const foundLocation = a.attributes.some((attr) => {
        if (attr.type === Attribute.TypeEnum.Point && locationRegex.test(attr.name)) {
          // e.g. "-34.214859;19.134784"
          const point = attr.value.split(';').map((v) => Number(v));
          if (point.length === 2) {
            coords = { lat: point[0], lng: point[1] };
            return true; // early break out of some() loop
          }
        }
      });

      // Search asset for name entry
      const nameRegex = /name/i;
      const foundName = a.attributes.some((attr) => {
        if (attr.type === Attribute.TypeEnum.Open && nameRegex.test(attr.name)) {
          assetName = attr.value;
          return true;
        }
      });

      // If we have both, stop looking
      if (foundLocation && foundName) {
        return true;
      }
    });

    if (!coords) {
      reject(new Error('invalid location coordinates'));
    } else {
      const mapLocation = new MapLocation().withLatLngLiteral(coords);
      mapLocation.assetName = assetName;
      mapLocation.fromAsset = true;
      this.lookupAddress(coords).then(
        (textAddress) => {
          mapLocation.withAddress(textAddress);
          resolve(mapLocation);
        },
        (_) => resolve(mapLocation)
      );
    }
  }
}
